/**
 * Arlo & Co — Variant-Aware Uploadcare
 * When a Shopify variant is selected, checks window.ArloProductConfig.uploadcareVariants
 * for a matching override and updates the uploader's max images (and crop if set).
 *
 * Requires: arlo-product-config.js loaded first.
 * Primary event: Artisan theme fires 'product:variant-change' with e.detail.variant
 */

(function () {
  'use strict';

  // ── Get overrides for current product ────────────────────────────────────────
  function getOverrides() {
    var config = window.ArloProductConfig;
    if (!config || !config.uploadcareVariants) return null;

    // Try handle from ShopifyAnalytics first, then URL
    var handle = (window.ShopifyAnalytics &&
                  window.ShopifyAnalytics.meta &&
                  window.ShopifyAnalytics.meta.product &&
                  window.ShopifyAnalytics.meta.product.handle) || null;
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m ? m[1] : null;
    }
    if (!handle) return null;

    return config.uploadcareVariants[handle] || null;
  }

  // ── Apply an override to the uploader ────────────────────────────────────────
  function applyOverride(override) {
    var root = document.getElementById('arlo-uploader');
    if (root) {
      if (override.maxImages != null) root.setAttribute('data-max-images', String(override.maxImages));
      if (override.crop)              root.setAttribute('data-crop', override.crop);
    }

    // Legacy Uploadcare widget fallback
    var widgetInput = document.getElementById('uploader');
    if (widgetInput) {
      if (override.maxImages != null) {
        widgetInput.setAttribute('data-multiple-max', override.maxImages);
        widgetInput.setAttribute('data-multiple-min', override.maxImages);
      }
      if (override.crop) widgetInput.setAttribute('data-crop', override.crop + ' minimum');
      if (window.uploadcare) {
        try { var w = uploadcare.Widget(widgetInput); if (w) w.value(null); } catch (e) {}
      }
    }
  }

  // ── Find override by variant object (has .id and .title) ─────────────────────
  function findOverrideByVariant(variant) {
    var overrides = getOverrides();
    if (!overrides || !overrides.length) return null;

    var vid = String(variant.id);

    // 1. Match by stored variant ID
    for (var i = 0; i < overrides.length; i++) {
      if (overrides[i].variantId && String(overrides[i].variantId) === vid) {
        return overrides[i];
      }
    }

    // 2. Match by variant title (reliable since titles are set by merchant)
    if (variant.title) {
      for (var j = 0; j < overrides.length; j++) {
        if (overrides[j].variantTitle === variant.title) {
          return overrides[j];
        }
      }
    }

    return null;
  }

  // ── Find override by variant ID only (fallback for non-event paths) ──────────
  function findOverrideById(variantId) {
    var overrides = getOverrides();
    if (!overrides || !overrides.length) return null;
    var vid = String(variantId);
    for (var i = 0; i < overrides.length; i++) {
      if (overrides[i].variantId && String(overrides[i].variantId) === vid) {
        return overrides[i];
      }
    }
    // Try ShopifyAnalytics for title fallback
    var variants = window.ShopifyAnalytics &&
                   window.ShopifyAnalytics.meta &&
                   window.ShopifyAnalytics.meta.product &&
                   window.ShopifyAnalytics.meta.product.variants || [];
    for (var k = 0; k < variants.length; k++) {
      if (String(variants[k].id) === vid) {
        return findOverrideByVariant(variants[k]);
      }
    }
    return null;
  }

  // ── Watch for variant changes ─────────────────────────────────────────────────
  function watchVariants() {

    // Only apply the variant override on initial page load if the URL explicitly
    // specifies a variant (i.e. the user navigated directly to that variant).
    // A fresh product page open auto-selects the first variant and fires
    // product:variant-change immediately — we want the Liquid default to stand
    // in that case so the page starts with the configured default slot count.
    var urlVariantId = (function () {
      var m = window.location.search.match(/[?&]variant=(\d+)/);
      return m ? m[1] : null;
    })();

    // After the first user-triggered change, all subsequent changes apply freely.
    var _userHasChanged = false;

    // PRIMARY: Artisan theme fires 'product:variant-change' with full variant object
    document.addEventListener('product:variant-change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;

      if (!_userHasChanged && !e.detail.userTriggered) {
        // This is the auto-selection on page load (not triggered by a user click).
        // Only apply if the URL had ?variant= pointing to this variant.
        if (!urlVariantId || String(variant.id) !== urlVariantId) return;
      }

      _userHasChanged = true;
      var override = findOverrideByVariant(variant);
      if (override) applyOverride(override);
    });

    // FALLBACK: other themes
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && (e.detail.variant || e.detail);
      if (variant && variant.id) {
        _userHasChanged = true;
        var override = findOverrideByVariant(variant);
        if (override) applyOverride(override);
      }
    });

    // FALLBACK: hidden input[name="id"] value setter intercept
    var form = document.querySelector('form[action*="/cart/add"]');
    var variantInput = form && form.querySelector('input[name="id"]');
    if (variantInput) {
      var lastId = variantInput.value;
      var descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      var originalSet = descriptor.set;
      Object.defineProperty(variantInput, 'value', {
        configurable: true,
        get: descriptor.get,
        set: function (v) {
          originalSet.call(this, v);
          if (String(v) !== String(lastId)) {
            lastId = String(v);
            _userHasChanged = true;
            var override = findOverrideById(v);
            if (override) applyOverride(override);
          }
        }
      });
      variantInput.addEventListener('change', function () {
        if (variantInput.value !== lastId) {
          lastId = variantInput.value;
          _userHasChanged = true;
          var override = findOverrideById(variantInput.value);
          if (override) applyOverride(override);
        }
      });

      // Apply initial variant only if URL specifies it explicitly
      if (urlVariantId && variantInput.value && String(variantInput.value) === urlVariantId) {
        var initOverride = findOverrideById(variantInput.value);
        if (initOverride) applyOverride(initOverride);
      }
    }
  }

  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb();
    if (attempts > 30) return;
    setTimeout(function () { waitForConfig(cb, attempts + 1); }, 100);
  }

  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(watchVariants);
  });

})();
